import * as Dialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'
import { useTranslation } from '../localization/useTranslation'
import { usePortalContainer } from '../providers/portal'
import { useAuthStore } from '../store/auth'
import { IconX } from '../ui/icons'
import { OverlayReporter } from './OverlayReporter'

/**
 * Trade-in terms & conditions (mockup `#terms-modal`). Radix Dialog portalled into the
 * shadow-root overlay node. `onAccept` ticks the consent box on the Details form.
 *
 * The copy references the shop's brand name via the `{brand}` placeholder in each string; we pass
 * `config.brandName`, falling back to a generic word (`terms.brandFallback`) when it isn't set.
 */
export function TermsDialog({ trigger, onAccept }: { trigger: ReactNode; onAccept: () => void }) {
  const container = usePortalContainer()
  const { t } = useTranslation()
  const brandName = useAuthStore((s) => s.config.brandName)
  const brand = brandName?.trim() || t('terms.brandFallback')

  return (
    // modal={false}: Radix's scroll-lock (react-remove-scroll) blocks the mouse wheel inside our
    // shadow root (it can't resolve the wheel target across the boundary). Disabling it restores
    // native wheel scrolling of the content; scrim/Escape/outside-click still close it.
    <Dialog.Root modal={false}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal container={container ?? undefined}>
        {/* Plain scrim, not Dialog.Overlay: Radix renders Overlay as null when modal={false}. As a
            direct Portal child it's still open-state gated, and clicking it closes the dialog. */}
        <div className="modal-scrim" aria-hidden />
        <Dialog.Content className="modal" aria-describedby={undefined}>
          <OverlayReporter />
          <div className="modal-head">
            <Dialog.Title asChild>
              <h2>{t('terms.title')}</h2>
            </Dialog.Title>
            <Dialog.Close className="icon-btn" aria-label={t('terms.close')}>
              <IconX />
            </Dialog.Close>
          </div>

          <div className="modal-body">
            <p>{t('terms.introLead', { brand })}</p>
            <p>{t('terms.introAccept', { brand })}</p>
            <p>{t('terms.introDonate')}</p>

            <h3>{t('terms.requirementsTitle')}</h3>
            <p>{t('terms.requirementsBody', { brand })}</p>

            <h3>{t('terms.howToTitle')}</h3>
            <p>{t('terms.howToRegister', { brand })}</p>
            <p>{t('terms.howToAdjust')}</p>
            <p>{t('terms.howToLabel', { brand })}</p>
            <p>{t('terms.howToFinal')}</p>

            <h3>{t('terms.voucherTitle')}</h3>
            <p>{t('terms.voucherIssue')}</p>
            <p>{t('terms.voucherValidity', { brand })}</p>

            <p>{t('terms.closing')}</p>
          </div>

          <div className="modal-foot">
            <Dialog.Close asChild>
              <button type="button" className="btn btn-ghost">
                {t('terms.close')}
              </button>
            </Dialog.Close>
            <Dialog.Close asChild>
              <button type="button" className="btn btn-primary" onClick={onAccept}>
                {t('terms.accept')}
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
